import type {
  Canal,
  Catalogo,
  Cotizacion,
  Pieza,
  Producto,
  Sitio,
  TipoPieza,
  UnidadMedida,
  UnidadesPieza,
  Unidad,
  Vendedor,
} from "./types";
export const id = () => crypto.randomUUID();
// Categorías de garantía con sus años sugeridos (null = el vendedor los
// escribe). La lista general de cada cotización nueva parte de estas, y las
// de sitio y pieza las ofrecen como atajo.
export const CATEGORIAS_GARANTIA: [string, number | null][] = [
  ["Sistema eléctrico", 2],
  ["Acrílicos", null],
  ["Lonas, vinilos, sintra", 2],
  ["Partes metálicas (anclajes)", null],
];

// Entrega de la cotización. Cada envío a n8n es de un solo canal: "ambos"
// son dos envíos (WhatsApp y correo) del mismo PDF.
export const CANALES: Canal[] = ["pdf", "whatsapp", "correo", "ambos"];
export const NOMBRE_CANAL: Record<Canal, string> = {
  pdf: "Solo PDF",
  whatsapp: "WhatsApp",
  correo: "Correo",
  ambos: "WhatsApp y correo",
};
export type CanalEnvio = "whatsapp" | "correo";
export const CANALES_ENVIO: Record<Canal, CanalEnvio[]> = {
  pdf: [],
  whatsapp: ["whatsapp"],
  correo: ["correo"],
  ambos: ["whatsapp", "correo"],
};
const CORREO_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Falta algo para entregar por el canal elegido (null si está todo).
// El WhatsApp de la cotización le llega al COMERCIANTE, a su contacto del CRM
// (oct-2026), y él se la reenvía al cliente desde su WhatsApp: no hace falta
// el teléfono del cliente, sí el contacto del cliente (la oportunidad cuelga
// de él) y el WhatsApp del comerciante. whatsapp null = sin contacto asignado;
// undefined = perfil aún sin cargar (decide el servidor).
export function errorDestino(
  c: Cotizacion,
  vendedor?: Pick<Vendedor, "whatsapp">,
): string | null {
  // El PDF solo imprime la forma de pago si se eligió, y el equipo entregaba
  // sin elegirla sin darse cuenta: es obligatoria para entregar, también
  // con «Solo PDF».
  if (!c.condiciones.forma_pago.trim())
    return "Elige la forma de pago en «Margen y envío» antes de entregar: sin ella el PDF sale sin condiciones de pago.";
  const canales = CANALES_ENVIO[c.canal];
  if (!canales.length) return null;
  if (
    !c.contacto ||
    (canales.includes("correo") && !CORREO_RE.test(c.correoEnvio))
  )
    return "Elige un contacto y verifica el destino del envío.";
  if (canales.includes("whatsapp") && vendedor?.whatsapp === null)
    return "Tu usuario todavía no tiene su WhatsApp configurado en el CRM. Pide a Invers que lo asigne; mientras tanto usa «Solo PDF» o «Correo».";
  return null;
}
// "WhatsApp a tu número … · Correo al cliente: a@b.ec" para la confirmación.
export const destinoEnvio = (
  c: Cotizacion,
  vendedor?: Pick<Vendedor, "whatsapp">,
) =>
  CANALES_ENVIO[c.canal]
    .map((k) =>
      k === "correo"
        ? `Correo al cliente: ${c.correoEnvio}`
        : `WhatsApp a tu número${
            vendedor?.whatsapp?.telefono ? ` ${vendedor.whatsapp.telefono}` : ""
          }, para que se la reenvíes al cliente desde tu WhatsApp`,
    )
    .join(" · ");
export const nuevaPieza = (): Pieza => ({
  id: id(),
  familia: "",
  variante: "",
  material: "",
  iluminacion: "N/A",
  precioUnitario: null,
  unidad: "m2",
  anchoCm: 0,
  altoCm: 0,
  profundidadCm: 0,
  // Ancho y alto en metros, profundidad en cm: lo habitual en planta.
  unidades: { ancho: "m", alto: "m", profundidad: "cm" },
  cantidad: 1,
  precioContratado: null,
  precioAjustado: null,
  margenOverride: null,
  descuentoPct: 0,
});
export const nuevoProducto = (): Producto => ({
  id: id(),
  nombre: "",
  imagenes: [],
  piezas: [nuevaPieza()],
});
export const nuevoSitio = (): Sitio => ({
  id: id(),
  nombre: "",
  direccion: "",
  ciudad: "",
  sede: "",
  margenOverride: null,
  productos: [nuevoProducto()],
  addons: [],
  logistica: {
    incluida: false,
    visitas: 1,
    costoPorVisita: 0,
    totalManual: null,
  },
});
export const nuevaCotizacion = (): Cotizacion => ({
  numero: `BORRADOR-${id().slice(0, 8)}`,
  numeroOficial: null,
  numeroBaseOficial: null,
  registroId: null,
  revision: null,
  cliente: {
    nombre: "",
    encargado: "",
    nombre_proyecto: "",
    direccion_facturacion: "",
    cliente_contrato: "ninguno",
  },
  contacto: null,
  condiciones: { forma_pago: "", tiempo_entrega: "", validez_oferta: "" },
  // Apagado por defecto (capacitación 03-oct-2026): encendido inflaba los
  // precios sin que el comercial lo notara. Se activa a mano en el resumen.
  margenActivo: false,
  margen: 0.5,
  descuento: 0,
  ivaIncluido: false,
  ivaPct: 15,
  formatoPdf: "consolidado",
  incluirGarantias: true,
  mostrarValorUnitario: true,
  garantias: CATEGORIAS_GARANTIA.map(([categoria, anios]) => ({
    id: id(),
    categoria,
    anios,
    notas: "",
    // El vendedor elige qué garantías ofrece; ninguna va marcada de entrada.
    incluida: false,
  })),
  sitios: [nuevoSitio()],
  simulacionUrl: "",
  fachadaUrl: "",
  canal: "pdf",
  correoEnvio: "",
  telefonoEnvio: "",
});
// Etapas comerciales del manual ("Cambiar estado"). Reemplazada no se elige:
// la asigna el backend al crear una revisión -R1, -R2…
export const ESTADOS_COTIZACION = [
  "Vigente",
  "GENERANDO BRIEF",
  "GENERANDO PRESENTACION",
  "COTIZACION EN PROCESO (levantamiento)",
  "COTIZACION EN REVISION CLIENTE",
  "NEGOCIACION (ajustes de cotización)",
  "ACEPTACION VERBAL O POR ESCRITO",
  "OP EN PRODUCCION",
  "EN INSTALACION",
  "FACTURADO POR COBRAR",
  "FACTURADO Y COBRADO",
  "COTIZACION NEGADA",
] as const;
// Pasar a Perdida en el CRM exige motivo (spec de Gerencia DV); el backend
// también lo exige.
export const ESTADOS_CON_MOTIVO: readonly string[] = ["COTIZACION NEGADA"];
export const seleccionarTipo = (
  pieza: Pieza,
  tipo: TipoPieza,
  contratado: number | null,
): Pieza => ({
  ...pieza,
  ...tipo,
  precioUnitario: tipo.precio,
  precioContratado: contratado,
  precioAjustado: null,
  catalogoEspecial: tipo.catalogoEspecial ?? null,
  materiales: tipo.materiales ?? null,
  materialesReferencia: tipo.materialesReferencia ?? null,
  personalizado: !!tipo.personalizado,
  esPersonalizada: !!tipo.esPersonalizada,
});
export const normalizar = (s: string) =>
  s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

// Formatos de entrega del PDF (select del paso 3). "Un documento por sitio"
// se quitó: n8n nunca lo implementó y salía igual al consolidado.
export const FORMATO_RESUMEN = "resumen_zonas";
export const FORMATOS_PDF = [
  { valor: "consolidado", etiqueta: "Resumen consolidado" },
  { valor: FORMATO_RESUMEN, etiqueta: "Resumen por tienda y zona" },
];
// Columna del resumen por tienda y zona. Se agrupa sin distinguir
// mayúsculas, tildes ni espacios repetidos — misma clave que usa el nodo
// "Construir HTML del PDF" de cotizador-generar-pdf.json.
export const columnaResumen = (pr: Producto) =>
  pr.columnaResumen?.trim() || pr.nombre.trim() || "(sin nombre)";
export const claveColumna = (s: string) => normalizar(s).replace(/\s+/g, " ");
export function columnasResumen(sitios: Sitio[]): string[] {
  const columnas = new Map<string, string>();
  for (const s of sitios)
    for (const pr of s.productos) {
      const etiqueta = columnaResumen(pr);
      if (!columnas.has(claveColumna(etiqueta)))
        columnas.set(claveColumna(etiqueta), etiqueta);
    }
  return [...columnas.values()];
}
export const slugCiudad = (s: string) =>
  s.toLowerCase().replace(/\s|\(|\)/g, "");
export const moneda = (n: number) =>
  new Intl.NumberFormat("es-EC", { style: "currency", currency: "USD" }).format(
    n,
  );
// Cómo marca el catálogo "no aplica" (p. ej. la iluminación de una pieza sin
// luz). Se omite al mostrarlo: "· N/A" o "+N/A" no le dice nada a nadie. La
// plantilla del PDF en n8n usa la misma expresión.
const NO_APLICA =
  /^(n\/?a|no aplica|ninguno|ninguna|sin iluminaci[oó]n|-+|—)$/i;
export const esNoAplica = (t: string) => NO_APLICA.test(t.trim());
const UNIDADES_VALIDAS = ["m", "cm", "mm"];
export const esUnidad = (u: unknown): u is UnidadMedida =>
  UNIDADES_VALIDAS.includes(String(u));
// Unidades con las que se muestra cada medida de la pieza. Las piezas
// guardadas antes de separarlas solo traen unidadMedida (una para todo) y se
// siguen viendo como se escribieron.
export function unidadesDe(p: Pieza): UnidadesPieza {
  if (p.unidades) return p.unidades;
  const u = p.unidadMedida ?? "cm";
  return { ancho: u, alto: u, profundidad: u };
}
// Precio de contrato del cliente para un tipo de pieza, o null. Lo usan el
// selector del catálogo y la carga de piezas por imagen.
export function precioDeContrato(
  catalogo: Catalogo | undefined,
  marca: string,
  tipo: Pick<TipoPieza, "familia" | "variante">,
): number | null {
  return (
    catalogo?.preciosContrato.find(
      (p) =>
        normalizar(p.clienteContrato) === normalizar(marca) &&
        p.familia === tipo.familia &&
        p.variante === tipo.variante,
    )?.precioContratado ?? null
  );
}
// Catálogo con el que cotiza este cliente: su catálogo especial (Fybeca,
// SanaSana, OkiDoki: piezas propias con código de artículo y sin recargos)
// si lo tiene, o el general. Una sola fuente para el selector del catálogo y
// la carga de piezas por imagen: si no, la IA elegía del general y se perdía
// el precio especial (auditoría de Codex, 2026-10-02).
export function catalogoEfectivo(data: Catalogo, marca: string): TipoPieza[] {
  const unidades: Record<string, Unidad> = {
    M2: "m2",
    CM2: "cm2",
    ML: "ml",
    CENTIMETRO: "cm_lineal",
    UNIDAD: "unidad",
    "N/A": "unidad",
  };
  const especial = data.catalogoClienteEspecial.filter(
    (p) => normalizar(p.clienteMarca) === normalizar(marca),
  );
  return especial.length
    ? especial.map((p) => ({
        familia: p.familia,
        variante: p.nombrePieza,
        material: p.codigoArticulo,
        iluminacion: "N/A",
        precio: unidades[p.unidad.trim().toUpperCase()]
          ? p.precioUnitario
          : null,
        unidad: unidades[p.unidad.trim().toUpperCase()] ?? "unidad",
        catalogoEspecial: {
          id: p.id,
          codigoArticulo: p.codigoArticulo,
          clienteMarca: p.clienteMarca,
          revisar: p.revisar || !unidades[p.unidad.trim().toUpperCase()],
          revisarMotivo: unidades[p.unidad.trim().toUpperCase()]
            ? p.revisarMotivo
            : "Unidad no soportada. Solicita un precio total manual a Producción DV.",
        },
      }))
    : data.catalogo;
}
